/**
 * Conversation invite service — invite a human to ONE chat group (not your account).
 * A shareable, per-chat invite link (the chat /join-chat flow).
 */
import { callFunction } from '../utils/apiClient';

export interface ConversationInviteLink {
  token: string;
  inviteUrl: string;
  expiresAt: string;
}

export interface ActiveInvite {
  id: string;
  token: string;
  inviteUrl: string;
  expiresAt: string;
  /** New people who joined via this link. */
  useCount?: number;
  createdAt?: string;
}

/** Owner: generate a shareable link to this chat (optionally choose how long it lasts). */
export const createConversationInviteLink = (conversationId: string, expiryDays?: number) =>
  callFunction<ConversationInviteLink>('createConversationInviteLink', { conversationId, expiryDays });

/** Owner: list this chat's active invite links. */
export const listConversationInvites = (conversationId: string) =>
  callFunction<{ invites: ActiveInvite[] }>('listConversationInvites', { conversationId }).then((r) => r.invites || []);

/** Owner: revoke an invite link so it can no longer be used. */
export const revokeConversationInvite = (inviteId: string) =>
  callFunction<{ revoked: boolean }>('revokeConversationInvite', { inviteId });

/** PUBLIC — preview an invite (who invited you, to what) before signing in. */
export const getConversationInvite = (token: string) =>
  callFunction<{ valid: boolean; inviterName?: string; conversationTitle?: string }>(
    'getConversationInvite', { token }, { requireAuth: false },
  );

/** Join the chat via the link (requires auth). */
export const acceptConversationInvite = (token: string) =>
  callFunction<{ conversationId: string; alreadyMember: boolean }>('acceptConversationInvite', { token });
